import { PromotionStatus, getPromotionStatusConfig } from '../value-objects/PromotionStatus';

export interface LeaderboardEntryProps {
  position: number;
  studentId: string;
  studentName: string;
  studentAvatar?: string;
  weekXP: number;
  currentStreak: number;
  isCurrentUser: boolean;
  promotionStatus: PromotionStatus;
  positionChange: number;
  xpDifferenceToAbove?: number;
  xpDifferenceToBelow?: number;
}

export class LeaderboardEntry {
  private readonly props: LeaderboardEntryProps;

  constructor(props: LeaderboardEntryProps) {
    this.props = props;
  }

  get position(): number {
    return this.props.position;
  }

  get studentId(): string {
    return this.props.studentId;
  }

  get studentName(): string {
    return this.props.studentName;
  }

  get studentAvatar(): string | undefined {
    return this.props.studentAvatar;
  }

  get weekXP(): number {
    return this.props.weekXP;
  }

  get currentStreak(): number {
    return this.props.currentStreak;
  }

  get isCurrentUser(): boolean {
    return this.props.isCurrentUser;
  }

  get promotionStatus(): PromotionStatus {
    return this.props.promotionStatus;
  }

  get positionChange(): number {
    return this.props.positionChange;
  }

  get xpDifferenceToAbove(): number | undefined {
    return this.props.xpDifferenceToAbove;
  }

  get xpDifferenceToBelow(): number | undefined {
    return this.props.xpDifferenceToBelow;
  }

  /**
   * Get medal emoji for top 3
   */
  getMedal(): string | null {
    if (this.props.position === 1) return '🥇';
    if (this.props.position === 2) return '🥈';
    if (this.props.position === 3) return '🥉';
    return null;
  }

  /**
   * Check if in top 3
   */
  isTopThree(): boolean {
    return this.props.position <= 3;
  }

  /**
   * Check if in promotion zone
   */
  willPromote(): boolean {
    return this.props.promotionStatus === 'PROMOTION_ZONE';
  }

  /**
   * Check if in demotion zone
   */
  willDemote(): boolean {
    return this.props.promotionStatus === 'DEMOTION_ZONE';
  }

  /**
   * Check if position improved
   */
  hasImproved(): boolean {
    return this.props.positionChange > 0;
  }

  /**
   * Check if position worsened
   */
  hasWorsened(): boolean {
    return this.props.positionChange < 0;
  }

  /**
   * Get position change display (e.g., "↑3", "↓2", "-")
   */
  getPositionChangeDisplay(): string {
    if (this.props.positionChange > 0) {
      return `↑${this.props.positionChange}`;
    }
    if (this.props.positionChange < 0) {
      return `↓${Math.abs(this.props.positionChange)}`;
    }
    return '-';
  }

  /**
   * Get position change color class
   */
  getPositionChangeColor(): string {
    if (this.props.positionChange > 0) return 'text-green-500';
    if (this.props.positionChange < 0) return 'text-red-500';
    return 'text-gray-400';
  }

  /**
   * Get promotion status config
   */
  getPromotionStatusConfig() {
    return getPromotionStatusConfig(this.props.promotionStatus);
  }

  /**
   * Get row background class based on status
   */
  getRowBackgroundClass(): string {
    if (this.props.isCurrentUser) {
      return 'bg-violet-50 dark:bg-violet-900/20';
    }
    if (this.props.promotionStatus === 'PROMOTION_ZONE') {
      return 'bg-green-50/50 dark:bg-green-900/10';
    }
    if (this.props.promotionStatus === 'DEMOTION_ZONE') {
      return 'bg-red-50/50 dark:bg-red-900/10';
    }
    return '';
  }

  /**
   * Check if has active streak
   */
  hasActiveStreak(): boolean {
    return this.props.currentStreak >= 2;
  }

  toPlainObject(): LeaderboardEntryProps {
    return { ...this.props };
  }
}
